import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isProTier } from '@/lib/tier';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PremiumUpsellCard } from '@/components/PremiumUpsellCard';
import { MessageSquare, Send, Loader2, Wind, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function CoachChat() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isPro = isProTier(user?.subscription_tier);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const suggestions = t('coach.suggestions', { returnObjects: true });
  const suggestionList = Array.isArray(suggestions) ? suggestions : [];

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    const newMsgs = [...messages, { role: 'user', content: msg }];
    setMessages(newMsgs);
    setBusy(true);
    try {
      const { data } = await api.post('/coach/chat', { message: msg, analysis_id: analysisId || null, history: messages, lang: (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2) });
      setMessages([...newMsgs, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      if (err.response?.status === 402) { toast.error(t('coach.proFeature')); }
      else toast.error(t('coach.busy'));
      setMessages(newMsgs);
    } finally { setBusy(false); }
  };

  if (!isPro) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#FF6B35]/12 flex items-center justify-center mx-auto"><MessageSquare className="w-7 h-7 text-[#FF6B35]" /></div>
          <h1 className="font-display text-4xl mt-5">{t('coach.paywallTitle')}</h1>
          <p className="text-muted-foreground mt-2">{t('coach.paywallDesc')}</p>
        </div>
        <PremiumUpsellCard testid="coach-upsell" title={t('coach.upsellTitle')} description={t('coach.upsellDesc')} icon={Sparkles} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 flex flex-col" style={{ minHeight: 'calc(100vh - 4rem)' }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-[#00B4D8]" /></div>
        <div>
          <h1 className="font-display text-3xl">{t('coach.title')}</h1>
          <p className="text-sm text-muted-foreground">{analysisId ? t('coach.discussingSession') : t('coach.askAnything')}</p>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto pb-4">
        {messages.length === 0 && (
          <div className="kite-card p-6">
            <div className="flex items-center gap-2 text-[#48CAE4]"><Wind className="w-4 h-4" /> <span className="font-medium">{t('coach.ready')}</span></div>
            <p className="text-sm text-muted-foreground mt-2">{t('coach.tryThese')}</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              {suggestionList.map((s) => <button key={s} onClick={() => send(s)} className="text-left text-sm p-3 rounded-xl bg-[#0A0A0A] border border-white/8 hover:border-[#48CAE4]/40 transition-colors" data-testid="coach-suggestion">{s}</button>)}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'bg-[#00B4D8] text-black' : 'kite-card'}`}>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</p>
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="kite-card px-4 py-3"><Loader2 className="w-4 h-4 text-[#48CAE4] animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-0 pt-3 bg-[#0A0A0A]">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('coach.inputPlaceholder')} className="bg-[#12161B] border-white/15" data-testid="coach-input" />
          <Button type="submit" disabled={busy || !input.trim()} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black rounded-xl px-5" data-testid="chat-send-button"><Send className="w-4 h-4" /></Button>
        </form>
      </div>
    </div>
  );
}
